<template>
  <v-card v-if="user" elevation="0">
    <v-flex v-if="loading">
      <v-skeleton-loader class="mx-auto" type="card"></v-skeleton-loader>
    </v-flex>
    <div v-else class="pa-0">
      <v-row>
        <v-col cols="12" sm="12" lg="4" class="pl-3 pt-0 pb-0 fill-height">
          <div>
            <CurriculumColorContainer v-if="user.useSyllabusRanks && user.curriculumProgress" :height="100"
              :curriculum-data="user.curriculumProgress">
            </CurriculumColorContainer>
            <BeltContainer v-else :height="100" :belt="user.belt" :not-bjj="user.notBjjUser"></BeltContainer>
            <div class="text-center" style="margin-top:-90px">
              <v-avatar size="190">
                <ImageDisplay v-if="user.userImage" :src="user.userImage" />
                <img v-else :src="assets() + '/default-profile-avatar.png'" />
              </v-avatar>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="12" lg="8">
          <v-row>
            <v-col>
              <v-list>
                <v-list-item class="text-left">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{ $t("message.name") }}</v-list-item-subtitle>
                    <v-list-item-title class="text-subtitle-1">
                      {{ user.fName }} {{ user.lName }}
                      <v-tooltip top v-if="user.isMinor">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" size="20"
                            :color="themeColor('primary')">mdi-baby-face-outline</v-icon>
                        </template>
                        <span>{{ $t("message.is_minor") }}</span>
                      </v-tooltip>
                      <v-tooltip top v-else-if="user.notBjjUser">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" size="20"
                            :color="themeColor('primary')">mdi-account-remove-outline</v-icon>
                        </template>
                        <span>{{ $t("message.not_bjj_user") }}</span>
                      </v-tooltip>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="text-left">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{ $t("message.rank") }}</v-list-item-subtitle>
                    <v-list-item-title class="text-subtitle-1" v-if="user.useSyllabusRanks && user.curriculumProgress">
                      <belt v-if="user.curriculumProgress.useBelts" :hide-image="true"
                        :belt="user.curriculumProgress.level" />
                      <span v-else>{{ user.curriculumProgress.level }}</span>
                      <span class="ml-1">({{ user.curriculumProgress.progress }}%)</span>
                    </v-list-item-title>
                    <v-list-item-title class="text-subtitle-1" v-else>
                      <belt :hide-image="true" v-if="!user.notBjjUser" :belt="user.belt" />
                      <div v-else>
                        {{ $t("message.not_bjj_user") }}
                      </div>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="text-left">
                  <v-list-item-content>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-list-item-subtitle v-bind="attrs" v-on="on">{{ $t("message.days_at_belt")
                        }}</v-list-item-subtitle>
                      </template>
                      <span>{{ $t("message.days_at_rank_explained") }}</span>
                    </v-tooltip>
                    <v-list-item-title class="text-subtitle-1">{{ daysAtBelt > 0 ? daysAtBelt : 0 }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col>
              <Nationality class="nationality-flag" :nationality-code="user.nationality" size="30px"></Nationality>
              <v-list>
                <v-list-item class="text-left">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{ $t("message.email") }}</v-list-item-subtitle>
                    <v-list-item-title class="text-subtitle-1 clickable" v-if="user.email">
                      {{ altEmail }}
                      <v-btn color="primary" class="ml-1" icon x-small
                        @click="copyEmail()"><v-icon>mdi-content-copy</v-icon></v-btn>
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" v-if="altEmail != user.email" color="primary"
                            size="20">mdi-account-multiple-outline</v-icon>
                        </template>
                        <span>{{ $t("message.uses_guardian_email") }}</span>
                      </v-tooltip>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="text-left" v-if="user.phone">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{ $t("message.phone") }}</v-list-item-subtitle>
                    <v-list-item-title class="text-subtitle-1 clickable" v-if="user.email">
                      {{ user.phone }}
                      <v-btn class="ml-1 mr-1" icon x-small color="primary"><a
                          :href="'tel:' + user.phone"><v-icon>mdi-phone-outline</v-icon></a></v-btn>
                      <v-btn color="primary" class="ml-1 mr-1" icon x-small
                        @click="copyPhone()"><v-icon>mdi-content-copy</v-icon></v-btn>
                      <v-tooltip v-if="user.hasApp" top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn @click="showAlert('info', user.hasApp.os + ' - ' + user.hasApp.deviceName)" icon
                            x-small class="ml-1" v-bind="attrs" v-on="on">
                            <v-icon>mdi-cellphone-check</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ user.hasApp.os }} - {{ user.hasApp.deviceName }}</span>
                      </v-tooltip>
                      <v-btn style="margin-left:5px" v-if="user.whatsapp" lass="ml-2" icon x-small
                        @click="openWhatsApp(user.whatsapp)"><img width="30"
                          :src="assets('icons/whatsapp.png')" /></v-btn>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="text-left">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{ $t("message.dob") }}</v-list-item-subtitle>
                    <v-list-item-title class="text-subtitle-1" v-if="user.dob"><dob-display
                        :dob="user.dob"></dob-display></v-list-item-title>
                    <v-list-item-title class="text-subtitle-1" v-else>--</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="text-left">
                  <v-list-item-content>
                    <v-list-item-subtitle>{{ $t("message.classes_at_rank") }}</v-list-item-subtitle>
                    <v-list-item-title class="text-subtitle-1">{{ classesAtBelt > 0 ? classesAtBelt : 0
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-icon v-if="isBirthday" size="30" color="accent" class="bday-icon">mdi-cake-variant-outline</v-icon>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import Api from "@/services/Api";
import Belt from "@/components/displays/BeltDisplay.vue";
import DobDisplay from "@/components/displays/DobDisplay.vue";
import Nationality from "@/components/displays/NationalityFlag.vue";
import BeltContainer from "@/components/displays/BeltContainer";
import CurriculumColorContainer from "@/components/displays/CurriculumColorContainer";

import {
} from '@vue-hero-icons/outline'

export default {
  props: ["user", "hideEmail", "isBirthday"],
  data() {
    return {
      loading: true,
      fName: null,
      lName: null,
      altEmail: null,
      countryCode: null,
      daysAtBelt: null,
      classesAtBelt: null,
      checkInTime: null,
    };
  },
  components: {
    Belt,
    DobDisplay,
    Nationality,
    BeltContainer,
    CurriculumColorContainer
  },
  methods: {
    copyEmail() {
      this.copyToClipboard(this.user.email);
    },
    copyPhone() {
      this.copyToClipboard(this.user.phone);
    },
    getMemberEmail() {
      let email = this.user.email;
      if (this.user.memberData) {
        if (this.user.memberData.guardianUser)
          email = this.user.memberData.guardianUser.email;
        else if (this.user.memberData.guardianEmail)
          email = this.user.memberData.guardianEmail;
      }
      return email;
    },
    beltBackground() {
      return 'background-color: ' + this.getColor(this.user.belt) + ';';
    },
    getStripesBackground() {
      if (this.user.belt) {
        const stripes = this.user.belt.charAt(this.user.belt.length - 1);
        if (!isNaN(stripes)) {
          //return 'background:repeating-linear-gradient(to right,white,white 10px,black 10px,black 20px';
        }
      }
      return 'background-color: black;'
    }
  },
  created() {
    if (this.user) {
      //Get days at belt and classes at belt
      this.fName = this.user.fName;
      this.lName = this.user.lName;
      this.altEmail = this.getMemberEmail();
      this.classesAtBelt = this.user.classesAtBelt;
      this.daysAtBelt = this.user.daysAtBelt;
      this.countryCode = this.user.countryCode;
      this.checkInTime = new Date(this.user.date);
      if (this.user.classesAtBelt == null || this.user.daysAtBelt == null) {
        //fetching info
        Api()
          .get(
            "admins/academy/" +
            this.$store.state.selectedAcademy +
            "/member/" +
            this.user._id
          )
          .then((response) => {
            const member = response.data;
            this.classesAtBelt = member.classesAtBelt;
            this.daysAtBelt = member.daysAtBelt;
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    }
  },
};
</script>

<style scoped>
.nationality-flag {
  float: right;
  right: 4px;
  top: -8px;
}

.bday-icon {
  float: right;
  margin-right: 6px;
  bottom: -8px;
}

.avatar-belt {
  height: 226px;
  border-bottom: 1px solid lightgray
}
</style>
